import { useEffect, useRef, useState } from "react";

const inputCls = "tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-400";

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
};

export default function SelectBox({ value, onChange, options, className, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setOpen((prev) => !prev);
  };

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div ref={containerRef} className={`tw-relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={handleToggle}
        className={`${inputCls} tw-flex tw-items-center tw-justify-between tw-text-left tw-bg-white ${disabled ? "tw-bg-gray-50 tw-text-gray-400 tw-cursor-not-allowed" : ""}`}
      >
        <span className="tw-text-gray-800">{selectedLabel}</span>
        <span className="tw-text-gray-400 tw-text-xs tw-ml-2">{disabled ? "" : open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div style={dropdownStyle} className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-overflow-hidden">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-border-0 hover:tw-bg-blue-50 ${
                o.value === value
                  ? "tw-bg-blue-50 tw-text-blue-600 tw-font-medium"
                  : "tw-text-gray-700"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
